import React, { useEffect } from "react";
import useAutocomplete from "@mui/material/useAutocomplete";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import { theme } from "$utils/theme/bataillog";

interface ItemType {
  title: string;
  type: string;
}

type Props = {
  defaultValues?: any;
  className?: string;
  inputClassName?: string;
  listBoxClassName?: string;
  listBoxLiClassName?: string;
  placeHolder?: string;
  getSelectedValue?: (params: any) => void;
  getSelectedItem?: (params: any) => void;
  defaultSelected?: any;
  activeOnFocus?: boolean;
};

function AutoComplete({
  className = "",
  inputClassName = "",
  listBoxClassName = "",
  listBoxLiClassName = "",
  placeHolder = "Rechercher sur tout le site ...",
  defaultValues = [],
  getSelectedValue,
  getSelectedItem,
  defaultSelected,
  activeOnFocus = false,
}: Props) {
  const classes = useStyles();
  const [options, setOptions] = React.useState<ItemType[]>([]);
  const [open, setOpen] = React.useState(false);
  const [inputVal, setInputValue] = React.useState("");
  const load = open && options.length === 0;

  useEffect(() => {
    let active = true;

    if (!(open && options.length === 0)) {
      return undefined;
    }
    if (typeof defaultValues === "function") {
      (async () => {
        try {
          const response = await (defaultValues as () => any)();
          if (active) {
            setOptions(response);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
          setOptions([]);
        }
      })();
    } else {
      setOptions(defaultValues);
    }

    return () => {
      active = false;
    };
  }, [defaultValues, open, options.length]);

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    popupOpen,
    inputValue,
    value,
  } = useAutocomplete({
    value: defaultSelected,
    id: "use-autocomplete-demo",
    options: activeOnFocus || inputVal.length >= 3 ? options : [],
    getOptionLabel: (option) => option.title ?? option.name,
    onChange: (option) => {
      if (typeof getSelectedItem === "function")
        getSelectedItem(
          groupedOptions[
            (option.target as any).getAttribute("data-option-index")
          ]
        );
    },
  });

  useEffect(() => {
    setOpen(popupOpen);
  }, [popupOpen]);

  useEffect(() => {
    setInputValue(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (typeof getSelectedValue === "function") getSelectedValue(value);
  }, [getSelectedValue, value]);

  useEffect(() => {
    if (defaultSelected) {
      setOptions(defaultValues);
    }
  }, [defaultSelected, defaultValues]);

  return (
    <div className={clsx(classes.inputRoot, className)}>
      <div className={classes.container} {...getRootProps()}>
        <input
          placeholder={placeHolder}
          className={clsx(classes.input, inputClassName)}
          {...getInputProps()}
        />
      </div>
      {load && <div className={clsx(classes.chargement)}>Chargement...</div>}
      {!load && groupedOptions.length > 0 ? (
        <ul
          className={clsx(listBoxClassName, classes.listbox)}
          {...getListboxProps()}
        >
          {groupedOptions.map((option, index) => (
            <li
              className={clsx(classes.listBoxItem, listBoxLiClassName)}
              {...getOptionProps({ option, index })}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: `${option.title}${
                  option.type
                    ? `<span data-option-index={index}>(${option.type})</span`
                    : ""
                }`,
              }}
            >
              {}
            </li>
          ))}
        </ul>
      ) : null}
      {!load &&
        groupedOptions.length === 0 &&
        open &&
        (activeOnFocus || inputVal.length >= 3) && (
          <div className={clsx(classes.chargement)}>
            Aucun résultat ne correspond à votre recherche
          </div>
        )}
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  label: {
    display: "block",
  },
  inputRoot: { position: "relative" },
  input: {
    flex: 1,
    boxSizing: "border-box",
  },
  container: {
    display: "flex",
    flex: 1,
  },
  listbox: {
    width: "100%",
    flex: 1,
    margin: 0,
    padding: 0,
    zIndex: 1,
    top: "100%",
    position: "absolute",
    listStyle: "none",
    backgroundColor: theme.color.white,
    overflow: "auto",
    maxHeight: "var(--seventyFiveVh)",
    border: `1px solid ${theme.color.gray5}`,
    color: theme.color.black,
    boxSizing: "border-box",
    '& li[data-focus="true"]': {
      backgroundColor: theme.color.blueLight,
      color: "white",
      cursor: "pointer",
      "& *": {
        color: "white",
      },
    },
  },
  listBoxItem: {
    padding: 6,
    fontSize: 14,
    "& span": {
      color: theme.color.gray5,
      fontSize: 12,
      marginLeft: 5,
    },
  },
  chargement: {
    composes: "$listbox",
    padding: [5, 3],
  },
}));

export default AutoComplete;

/*
const exampleReturn = [
  { title: "Sanofi", type: "Entreprices" },
  { title: "SNCF", type: "Entreprices" },
  { title: "Sebastien Gougeon", type: "SCM" },
  { title: "Sophie Marceau", type: "SCM" },
  { title: "Abraham Licoln", type: "SCM" },
  { title: "John Doe", type: "SCM" },
  { title: "EDF", type: "Entreprise" },
];
 */
